/**
 * Mirrors of Rust serde-tagged types that cross the Tauri command boundary
 * for the recordings feature. Keep these in lockstep with the Rust source —
 * a CI integration check (or a Phase D wire-format guard test) is the
 * trip-wire for drift.
 *
 * Closes Phase D retrospective finding H5: the React side previously
 * pattern-matched on shape (e.g. `(err as Record<string, unknown>).kind`),
 * which silently broke when a Rust variant was renamed.
 */

// ── RecordingStartError ──────────────────────────────────────────────────────
//
// Mirror of Rust `RecordingStartError` in
// `apps/lumen/core/src/commands/recording.rs:64`.
//
// Rust serde shape:
//   #[serde(tag = "kind", rename_all = "snake_case")]
//
// The `recording_start` Tauri command rejects its JS Promise with one of
// these objects. The wire-format guard test
// `recording_start_error_serialises_to_expected_wire_format`
// (commands/recording.rs:912) asserts the literal JSON shapes below.
export type RecordingStartError =
  | { kind: 'already_starting' }
  | { kind: 'requires_restart' }
  | { kind: 'enable_system_audio_half' }
  | { kind: 'screen_recording_denied' }
  | { kind: 'microphone_denied' }
  | { kind: 'microphone_not_determined' }
  | { kind: 'already_recording'; device_label: string }
  | { kind: 'unauthorized' }
  /**
   * The workspace is at or below the credit floor and the server refused the
   * create (402 `insufficient_credits`). `balance` can be NEGATIVE — the floor
   * is -500, not 0 — and is null when the server omitted the detail. Distinct
   * from the other 402, `workspace_read_only`, which maps to `other`.
   */
  | { kind: 'credit_limit_reached'; balance: number | null }
  | { kind: 'other'; message: string };

/**
 * Type guard for `RecordingStartError`. Useful when handling the rejection
 * of `invoke('recording_start', ...)` — the value comes through as
 * `unknown` and the discriminant `kind` is the safe entry point.
 *
 * Mirrors the snake_case serde tag emitted by the Rust side.
 */
export function isRecordingStartError(
  value: unknown,
): value is RecordingStartError {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  switch (v.kind) {
    case 'already_starting':
    case 'requires_restart':
    case 'enable_system_audio_half':
    case 'screen_recording_denied':
    case 'microphone_denied':
    case 'microphone_not_determined':
    case 'unauthorized':
      return true;
    case 'already_recording':
      return typeof v.device_label === 'string';
    case 'credit_limit_reached':
      return v.balance === null || typeof v.balance === 'number';
    case 'other':
      return typeof v.message === 'string';
    default:
      return false;
  }
}

// ── RecordingStopError ───────────────────────────────────────────────────────
//
// Mirror of Rust `RecordingStopError` in
// `apps/lumen/core/src/commands/recording.rs`.
//
// Rust serde shape:
//   #[serde(tag = "kind", rename_all = "snake_case")]
//
// The `recording_stop` Tauri command rejects its JS Promise with one of
// these objects. The wire-format guard test
// `recording_stop_error_serialises_to_expected_wire_format`
// (commands/recording.rs) asserts the literal JSON shapes below.
//
export type RecordingStopError =
  | { kind: 'not_found' }
  | { kind: 'io'; message: string }
  | { kind: 'other'; message: string };

/**
 * Type guard for `RecordingStopError`. Useful when handling the rejection
 * of `invoke('recording_stop', ...)`.
 */
export function isRecordingStopError(
  value: unknown,
): value is RecordingStopError {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  switch (v.kind) {
    case 'not_found':
      return true;
    case 'io':
    case 'other':
      return typeof v.message === 'string';
    default:
      return false;
  }
}

// ── RecordingDiscardError ────────────────────────────────────────────────────
//
// Mirror of Rust `RecordingDiscardError` in
// `apps/lumen/core/src/commands/recording.rs`.
//
// Rust serde shape:
//   #[serde(tag = "kind", rename_all = "snake_case")]
//
// The `recording_discard` Tauri command rejects its JS Promise with one of
// these objects. The wire-format guard test
// `recording_discard_error_serialises_to_expected_wire_format`
// (commands/recording.rs) asserts the literal JSON shapes below.
export type RecordingDiscardError =
  | { kind: 'not_found' }
  | { kind: 'io'; message: string }
  | { kind: 'other'; message: string };

/**
 * Type guard for `RecordingDiscardError`. Useful when handling the rejection
 * of `invoke('recording_discard', ...)`.
 */
export function isRecordingDiscardError(
  value: unknown,
): value is RecordingDiscardError {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  switch (v.kind) {
    case 'not_found':
      return true;
    case 'io':
    case 'other':
      return typeof v.message === 'string';
    default:
      return false;
  }
}

// ── RecordingMeta + RecordingStatus (entity slice mirrors) ───────────────────
//
// Client-side recording row shape — mirrors the server's `recordings` row
// (per session 1's schema + session 9's entity event payload). We
// deliberately omit server-only internal fields (client_temp_id once
// reconciled, audio_s3_key, jobs metadata).
//
// Source of truth: `apps/api/nexus/models/recording/core.rs::sync_row_to_json`.
// Any schema change there requires corresponding updates here + the
// entityStore reducer in `apps/lumen/src/lib/sync/entity-events/recording.ts`.
//
// D-16: title is NOT on RecordingMeta — it lives on the linked document.
// Read the title from entityStore.entityMeta using `entityId`.
//
// D-17: enhancedNotes is NOT on RecordingMeta — notes are applied to the
// document via UpdateEntity (Yjs). The client reads notes from document
// content, not from the recording row.

export type RecordingStatus =
  | 'recording'
  | 'uploading'
  | 'pending'
  | 'transcribing'
  | 'complete'
  | 'failed';

export type RecordingKind = 'meeting' | 'voice_memo';

/**
 * User-edited speaker label entry. Both fields are optional; the server
 * removes the entry entirely when both resolve to empty.
 *
 * Mirrors the Rust `SpeakerLabel` struct in
 * `apps/api/nexus/models/recording_speaker_labels.rs`. The struct uses
 * `#[serde(rename_all = "camelCase")]` and skips `None` fields.
 */
export type SpeakerLabel = {
  name?: string;
  email?: string;
};

export type RecordingMeta = {
  id: string;
  /** Linked notes document entity id. Null for recordings without a paired
   *  notes doc (rare; voice memos may set this). Server-emitted as `entityId`
   *  from sync_row_to_json — post entities-migration the linked doc IS an
   *  entity, so `entityId` reflects the unified semantic. */
  entityId: string | null;
  kind: RecordingKind;
  /**
   * UUID of the linked calendar event, when this recording was started from a
   * calendar entry. Null for ad-hoc recordings. Drives the attendee-suggestion
   * dropdown in the speaker-label editor.
   */
  calendarEventId: string | null;
  startedAt: string; // ISO 8601
  endedAt: string | null;
  durationSeconds: number | null;
  status: RecordingStatus;
  failureReason: string | null;
  audioRetentionUntil: string | null;
  pendingPurgeFrom: string | null;
  /**
   * Friendly speaker labels keyed by raw integer speaker_id (as string).
   * Empty when no rename has happened. The fallback display label is always
   * `"Speaker {speakerId}"` — see `SpeakerLabelEdit`.
   */
  speakerLabels: Record<string, SpeakerLabel>;
  /**
   * The manifest surface this recording was started from (design §3.6). Null
   * for recordings that predate the surface-binding manifest field, and for
   * ad-hoc recordings that own their entity rather than being handed a
   * surface. `useRecordingForSurface` matches this against a surface's own
   * id to resolve the binding from server-owned state, independent of
   * whether the client-side bind effect ever landed.
   */
  surfaceId: string | null;
};

/**
 * Statuses that indicate work is still happening server-side.
 * UI surfaces a spinner / progress indicator for these.
 */
export const IN_FLIGHT_STATUSES: ReadonlyArray<RecordingStatus> = [
  'recording',
  'uploading',
  'pending',
  'transcribing',
];

/**
 * Statuses where the recording reached a final state (complete or failed).
 */
export const TERMINAL_STATUSES: ReadonlyArray<RecordingStatus> = [
  'complete',
  'failed',
];

// ── Transcript types (GET /recordings/:id/transcript wire format) ─────────────
//
// Mirror of Rust `TranscriptSegment` + `Transcript` in
// `crates/recording-types/src/lib.rs`.
//
// IMPORTANT: These structs have NO `#[serde(rename_all = "camelCase")]` — the
// wire format uses snake_case field names (start_ms, end_ms, schema_version).
// The `speaker` field uses `#[serde(skip_serializing_if = "Option::is_none")]`
// so it is *absent* (not null) when diarization wasn't run for that segment.
//
// Wire-format guard test: types.test.ts
// `transcript_segment_wire_format_*` and `transcript_wire_format_*`

/**
 * One segment of a diarized transcript.
 * Wire field names are snake_case — matches Rust serde default (no rename_all).
 */
export type TranscriptSegment = {
  /** Raw integer speaker ID from Deepgram. Stable per recording — used as the
   *  key into `RecordingMeta.speakerLabels`. Absent when diarization didn't run. */
  speaker_id?: number;
  /** Display fallback label, e.g. "Speaker 0". Absent (not null) when
   *  diarization was not run. Kept for backwards compatibility — clients should
   *  prefer `speakerLabels[speaker_id]?.name` when present. */
  speaker?: string;
  start_ms: number;
  end_ms: number;
  text: string;
};

/**
 * The transcript `schema_version` this client understands. Mirrors
 * `TRANSCRIPT_SCHEMA_VERSION` in `crates/recording-types/src/lib.rs`; a blob
 * declaring any other version is rejected by `parseTranscript`.
 */
export const TRANSCRIPT_SCHEMA_VERSION = 1;

/**
 * Full transcript blob returned by GET /recordings/:id/transcript.
 * Stored as JSONB on the recordings row; set by the pipeline after Stage 1.
 */
export type Transcript = {
  /** Schema version — bump when the shape changes. Currently 1. */
  schema_version: number;
  segments: TranscriptSegment[];
  /** ISO 639-1 language code, e.g. "en". */
  language: string;
  /** Provider name, e.g. "deepgram". */
  provider: string;
};

/**
 * Type guard for `TranscriptSegment`. Validates the snake_case wire shape.
 */
export function isTranscriptSegment(value: unknown): value is TranscriptSegment {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v['start_ms'] !== 'number') return false;
  if (typeof v['end_ms'] !== 'number') return false;
  if (typeof v['text'] !== 'string') return false;
  if ('speaker' in v && typeof v['speaker'] !== 'string') return false;
  if ('speaker_id' in v && typeof v['speaker_id'] !== 'number') return false;
  return true;
}

/**
 * Type guard for `Transcript`. Validates the full transcript blob shape.
 */
export function isTranscript(value: unknown): value is Transcript {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v['schema_version'] !== 'number') return false;
  if (!Array.isArray(v['segments'])) return false;
  if (!(v['segments'] as unknown[]).every(isTranscriptSegment)) return false;
  if (typeof v['language'] !== 'string') return false;
  if (typeof v['provider'] !== 'string') return false;
  return true;
}
